import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

const NotificationCenter = ({ notifications = [], onNotificationDismiss, userRole = 'student' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const notificationRef = useRef(null);

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const getNotificationIcon = (type) => {
    const iconMap = {
      xp: 'Zap',
      badge: 'Award',
      achievement: 'Trophy',
      alert: 'AlertCircle',
      info: 'Info',
      success: 'CheckCircle',
      warning: 'AlertTriangle',
      error: 'XCircle'
    };
    return iconMap?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      xp: 'text-accent',
      badge: 'text-secondary',
      achievement: 'text-success',
      alert: 'text-warning',
      info: 'text-accent',
      success: 'text-success',
      warning: 'text-warning',
      error: 'text-error'
    };
    return colorMap?.[type] || 'text-foreground';
  };

  const handleDismiss = (notificationId) => {
    if (onNotificationDismiss) {
      onNotificationDismiss(notificationId);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef?.current && !notificationRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={notificationRef}>
      <button
        className="relative p-2 rounded-md hover:bg-muted transition-all duration-150"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-md shadow-elevation-3 z-[1100] max-h-[32rem] overflow-hidden flex flex-col animate-fade-in">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-popover-foreground">Notifications</h3>
            {notifications?.length > 0 && (
              <button
                className="text-sm text-accent hover:text-accent/80 transition-colors duration-150"
                onClick={() => notifications?.forEach(n => handleDismiss(n?.id))}
              >
                Clear all
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Icon name="Bell" size={48} className="text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No notifications yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {userRole === 'student' ? "You'll see XP gains and achievements here" :"You'll see system alerts and updates here"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    className={`p-4 hover:bg-muted transition-all duration-150 ${
                      !notification?.read ? 'bg-accent/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                        <Icon name={getNotificationIcon(notification?.type)} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-popover-foreground">
                          {notification?.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification?.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(notification?.timestamp)}
                          </span>
                          {!notification?.read && (
                            <span className="text-xs font-medium text-accent">New</span>
                          )}
                        </div>
                      </div>
                      <button
                        className="flex-shrink-0 p-1 rounded hover:bg-muted-foreground/10 transition-colors duration-150"
                        onClick={() => handleDismiss(notification?.id)}
                        aria-label="Dismiss notification"
                      >
                        <Icon name="X" size={16} className="text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;